import { Grid } from '@mui/material';
import {
  HSpacer,
  RoutalDialog,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import WarningIcon from '../assets/svg/WarningIcon';
import { RouteAvatar } from '../components/RouteAvatar';
import { Driver } from '../domain/driver/Driver';
import Button from '../react-components/Button';
import Text from '../react-components/Text';

type StopsToDriverDialogProps = {
  open: boolean;
  type: `assign` | `exchange`;
  drivers: Driver[];
  disabledDriverIds?: string[];
  setDialog: (...args: any[]) => any;
  handleInteractStops: (...args: any[]) => any;
};

export const StopsToDriverDialog = ({
  open,
  type,
  drivers,
  disabledDriverIds,
  setDialog,
  handleInteractStops,
}: StopsToDriverDialogProps) => {
  const [selectedDriver, setSelectedDriver] = useState<number | `missing` | undefined>(undefined);

  // Select the first driver to avoid empty select
  useEffect(() => {
    if (drivers.length > 0) {
      const notDisabledDrivers = drivers
        .map((d, idx: number) => ({ idx, data: d }))
        .filter((driver) => !(disabledDriverIds ?? []).includes(driver.data.id));
      if (notDisabledDrivers.length > 0) {
        setSelectedDriver(notDisabledDrivers[0].idx);
      } else {
        setSelectedDriver(`missing`);
      }

      // setSelectedDriver(0);
    }
  }, [disabledDriverIds, drivers]);

  const onInteractServices = (e: any) => {
    e.stopPropagation();
    if (selectedDriver !== undefined) {
      handleInteractStops(selectedDriver === `missing` ? selectedDriver : drivers[selectedDriver as number]);
    }
  };

  const getDriverLayout = (driver: Driver) => (
    <Grid item container direction="row" alignItems="center" wrap="nowrap">
      <RouteAvatar driver={driver} planDrivers={drivers} additionalStyles={{ marginRight: `8px` }} />

      <RoutalTooltip
        mouseEnterDelay={0.5}
        mouseLeaveDelay={0}
        id={driver.id}
        title={driver.label ?? `No name`}
        placement="top"
      >
        {driver.label && driver.label !== `` ? (
          <Text variant="body" style={{ cursor: `pointer` }}>
            {driver.label.length > 28 ? `${driver.label.substring(0, 28)}...` : `${driver.label}`}
          </Text>
        ) : (
          <Text variant="body" intlId="no_name" style={{ color: RoutalPalette.secondary00, cursor: `pointer` }} />
        )}
      </RoutalTooltip>
    </Grid>
  );

  const getMissingLayout = () => (
    <Grid item container direction="row" alignItems="center" wrap="nowrap">
      <WarningIcon color="primary" />

      <HSpacer small />

      <Text style={{ cursor: `pointer` }} intlId="planmap.textnotassigned" />
    </Grid>
  );

  const isOptionsDisabled = (driver: Driver) => {
    return disabledDriverIds?.includes(driver.id);
  };

  return (
    <RoutalDialog
      open={open}
      onCloseDialog={() => setDialog(undefined)}
      titleContent={<Text variant="h6" intlId={`services.${type}dialog.title`} />}
      titleRightContent={
        <Button
          variant="default"
          onClick={onInteractServices}
          intlId={`services.${type}dialog.button`}
          disabled={
            selectedDriver !== `missing` &&
            (selectedDriver === undefined ||
              !drivers[selectedDriver as number] ||
              drivers[selectedDriver as number].is_locked)
          }
        />
      }
      showFooter={false}
    >
      <Text intlId={`services.${type}dialog.subtitle`} />

      <VSpacer medium />

      <RoutalSelect
        id="select-driver"
        // style={{
        //   backgroundColor: `transparent`,
        //   border: `1px solid #999`,
        //   borderRadius: `4px`,
        //   color: `#393939`,
        //   height: `24px`,
        //   maxWidth: `300px`,
        // }}
        popoverProps={{
          options: [
            ...drivers.map(
              (driver, idx): RoutalSelectMultiSelectOption => ({
                key: `driver-${driver.id}`,
                // width: 300,
                onClick: () => {
                  setSelectedDriver(idx);
                },
                disabled: isOptionsDisabled(driver),
                content: getDriverLayout(driver),
              })
            ),
            {
              key: `missing-driver`,
              // width: 300,
              onClick: () => {
                setSelectedDriver(`missing`);
              },
              content: getMissingLayout(),
            },
          ],
        }}
      >
        {selectedDriver === `missing`
          ? getMissingLayout()
          : drivers.length > 0 && selectedDriver !== undefined && drivers[selectedDriver]
          ? getDriverLayout(drivers[selectedDriver])
          : undefined}
      </RoutalSelect>

      <VSpacer medium />
    </RoutalDialog>
  );
};
